import { IntervalConfig, IntervalType, ReportMetadata } from '../types';
import type { ListProject } from './listProject';
import { istSifaProjekt } from './projectType';

/**
 * Parst einen YYYY-MM-DD String als lokales Datum (kein UTC-Drift).
 *
 * Gibt `null` zurück, wenn:
 *   - der Input leer, undefined oder kein String ist
 *   - der String nicht dem ISO-Format YYYY-MM-DD entspricht
 *   - die Werte (Jahr/Monat/Tag) nicht plausibel sind
 *   - das Datum bei der Erzeugung "überlaufen" wäre (z.B. 2026-02-30)
 *
 * Akzeptiert auch ISO-Datums-Strings mit Zeit-Suffix (z.B. "2026-05-19T08:00:00Z"),
 * der Zeit-Teil wird dabei ignoriert.
 */
export const parseDateLocal = (dateStr: string | null | undefined): Date | null => {
    if (!dateStr || typeof dateStr !== 'string') return null;

    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    if (year < 1900 || year > 2200) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;

    const d = new Date(year, month - 1, day, 12, 0, 0, 0);

    // Rolling-Over abfangen (z.B. 2026-02-30 → würde sonst zu 2026-03-02 werden)
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;

    return d;
};

/** Kalendertag in Europe/Berlin — die Zone der Baustelle, nie die des Geraets (wie `buchungstag`, S5d). */
const BERLINER_TAG = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

/**
 * Der heutige Kalendertag in Europe/Berlin, `YYYY-MM-DD` — derselbe Tag, den
 * `buchungstag` einer jetzt gebuchten Leistung gibt. `new Date().toISOString()`
 * lieferte den UTC-Tag: zwischen 00:00 und 02:00 (MESZ) bzw. 01:00 (MEZ) also
 * gestern.
 */
export const heuteBerlin = (jetzt: Date = new Date()): string => {
    const teile = BERLINER_TAG.formatToParts(jetzt);
    const teil = (typ: string) => teile.find(p => p.type === typ)?.value ?? '';
    return `${teil('year')}-${teil('month')}-${teil('day')}`;
};

/**
 * Formatiert ein Date-Objekt als YYYY-MM-DD String ohne UTC-Drift.
 * Gibt '' zurück bei null oder Invalid Date.
 */
export const formatDateLocal = (date: Date | null): string => {
    if (!date || isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/** date + days als YYYY-MM-DD. */
const addDaysFormatted = (date: Date, days: number): string => {
    const next = new Date(date);
    next.setDate(date.getDate() + days);
    return formatDateLocal(next);
};

/** date + months als YYYY-MM-DD. Überlauf folgt JS-Date-Semantik (31.01. + 1M → 03.03.). */
const addMonthsFormatted = (date: Date, months: number): string => {
    const next = new Date(date);
    next.setMonth(date.getMonth() + months);
    return formatDateLocal(next);
};

/**
 * Mapping-Funktion: Wandelt eine Frequenz (1-7 Begehungen pro Woche)
 * in ein festes Wochentag-Pattern um.
 */
const getWeekdaysFromFrequency = (freq: number): number[] => {
    if (freq <= 0) return [3];
    if (freq >= 7) return [0, 1, 2, 3, 4, 5, 6];

    const patterns: Record<number, number[]> = {
        1: [3],
        2: [2, 4],
        3: [1, 3, 5],
        4: [1, 2, 4, 5],
        5: [1, 2, 3, 4, 5],
        6: [1, 2, 3, 4, 5, 6]
    };
    return patterns[freq] || [3];
};

/**
 * Erstes Vorkommen eines der Wochentage, beginnend bei baseDate + startOffset Tagen
 * (der Starttag selbst zählt mit). Suchfenster: 14 Tage, danach null.
 */
const findWeekdayOnOrAfter = (baseDateStr: string, weekdays: number[], startOffset: number): string | null => {
    if (!baseDateStr || !weekdays || weekdays.length === 0) return null;

    const baseDate = parseDateLocal(baseDateStr);
    if (!baseDate) return null;

    const checkDate = new Date(baseDate);
    checkDate.setDate(checkDate.getDate() + startOffset);

    for (let i = 0; i < 14; i++) {
        if (weekdays.includes(checkDate.getDay())) {
            return formatDateLocal(checkDate);
        }
        checkDate.setDate(checkDate.getDate() + 1);
    }
    return null;
};

/**
 * Berechnet das nächste Vorkommen eines der angegebenen Wochentage,
 * NACH dem baseDateStr (der Basistag selbst zählt nicht).
 */
export const getNextOccurrenceOf = (baseDateStr: string, weekdays: number[]): string | null =>
    findWeekdayOnOrAfter(baseDateStr, weekdays, 1);

/**
 * Mindestabstand in Tagen pro Intervalltyp, bevor bei bevorzugten Wochentagen
 * der nächste Termin gesucht wird. preferredWeekdays respektiert damit den
 * Intervalltyp (biweekly darf nicht 1 Tag später fällig werden).
 * daily und custom fehlen bewusst: daily ignoriert Präferenzen,
 * custom behandelt sie eigenständig (Gap 1, nur bei customDays <= 7).
 */
const PREFERRED_DAY_MIN_GAP: Partial<Record<IntervalType, number>> = {
    weekly: 5,
    biweekly: 8,
    monthly: 25,
    twice_monthly: 10,
};

export const calculateNextDueDate = (lastDateStr: string, config: IntervalConfig): string => {
    if (!lastDateStr || config.type === 'manual') return '';

    const date = parseDateLocal(lastDateStr);
    if (!date) return ''; // ← Defensiv: ungültiger Input gibt leeren String statt "NaN-NaN-NaN"

    const preferred = config.preferredWeekdays && config.preferredWeekdays.length > 0
        ? config.preferredWeekdays
        : null;

    const minGap = PREFERRED_DAY_MIN_GAP[config.type];
    if (preferred && minGap !== undefined) {
        const next = findWeekdayOnOrAfter(lastDateStr, preferred, minGap);
        if (next) return next;
    }

    switch (config.type) {
        case 'daily':
            return addDaysFormatted(date, 1);

        case 'weekly':
            return addDaysFormatted(date, 7);

        case 'biweekly':
            return addDaysFormatted(date, 14);

        case 'monthly':
            return addMonthsFormatted(date, 1);

        case 'twice_monthly': {
            if (date.getDate() <= 15) return addDaysFormatted(date, 14);
            const next = new Date(date);
            next.setMonth(date.getMonth() + 1);
            next.setDate(1);
            return formatDateLocal(next);
        }

        case 'custom':
            if (config.customDays) {
                if (config.customDays <= 7) {
                    const weekdays = preferred ?? getWeekdaysFromFrequency(config.customDays);
                    const next = findWeekdayOnOrAfter(lastDateStr, weekdays, 1);
                    if (next) return next;
                }
                return addDaysFormatted(date, config.customDays);
            }
            break;
    }

    // Final fallback
    return addDaysFormatted(date, 7);
};

/** Tage pro Intervall für die lineare Enddatum-Hochrechnung (twice_monthly ≈ 15). */
const PROJECTION_DAYS: Partial<Record<IntervalType, number>> = {
    daily: 1,
    weekly: 7,
    biweekly: 14,
    twice_monthly: 15,
    manual: 7,
};

/** Zählt ab start (zählt als 1. Termin) count Treffer auf den Wochentag-Pattern ab. */
const walkWeekdayPattern = (start: Date, count: number, weekdays: number[]): string => {
    let validDaysFound = 1;
    const cursor = new Date(start);
    while (validDaysFound < count) {
        cursor.setDate(cursor.getDate() + 1);
        if (weekdays.includes(cursor.getDay())) {
            validDaysFound++;
        }
    }
    return formatDateLocal(cursor);
};

export const calculateProjectedEndDate = (startDateStr: string, count: number, config: IntervalConfig): string => {
    if (!startDateStr || isNaN(count) || count <= 1) return startDateStr || '';

    const start = parseDateLocal(startDateStr);
    if (!start) return ''; // ← Defensiv

    const intervalsToAdd = count - 1;

    if (config.type === 'monthly') {
        return addMonthsFormatted(start, intervalsToAdd);
    }

    if (config.type === 'custom') {
        if (!config.customDays) return formatDateLocal(start);
        if (config.customDays > 7) return addDaysFormatted(start, intervalsToAdd * config.customDays);

        const weekdays = config.preferredWeekdays && config.preferredWeekdays.length > 0
            ? config.preferredWeekdays
            : getWeekdaysFromFrequency(config.customDays);
        return walkWeekdayPattern(start, count, weekdays);
    }

    return addDaysFormatted(start, intervalsToAdd * (PROJECTION_DAYS[config.type] ?? 0));
};

/**
 * Darf eine Leistungsbuchung den Begehungsrhythmus des Projekts fortschreiben?
 *
 * Gebucht wird auf einer Leistung mit `assignment = 'inspection'` aus zwei
 * voellig verschiedenen Anlaessen:
 *   - Eine Begehung hat stattgefunden. Dann ist das Buchungsdatum der neue
 *     Stand, und der Folgetermin gehoert neu gerechnet.
 *   - Ein NACHTRAG fuer die Abrechnung. Das Leistungsdatum liegt dann in der
 *     Vergangenheit — oft Monate. Die Menge stimmt, der Terminplan darf sich
 *     davon aber nicht bewegen.
 *
 * Ohne diese Unterscheidung erklaerte ein rueckdatierter Nachtrag das Projekt
 * fuer "zuletzt an jenem alten Tag begangen" und rechnete den Folgetermin
 * daraus neu — bei Projekt 6146 sprang der Termin dadurch vom 25.08.2026 auf
 * den 13.01.2026 zurueck, obwohl der Bericht vom 11.08. sauber archiviert war.
 *
 * Rueckgabe `null` heisst: nichts anfassen. Sonst das Paar, das Bericht und
 * Projektzeile gemeinsam bekommen — `date` bewusst auf Tagesgenauigkeit
 * normalisiert, denn der Buchungsdialog liefert einen vollen ISO-Zeitstempel
 * (Datum + Uhrzeit), waehrend `metadata.date` ueberall sonst YYYY-MM-DD ist.
 */
export const planInspectionScheduleUpdate = (
    bookingDateStr: string,
    currentReportDateStr: string | null | undefined,
    config: IntervalConfig,
): { date: string; nextScheduledDate: string } | null => {
    const booking = parseDateLocal(bookingDateStr);
    if (!booking) return null;

    // parseDateLocal setzt beide Seiten auf 12:00 Ortszeit — verglichen wird
    // damit auf den Tag, nicht auf die Uhrzeit aus dem Buchungsdialog.
    const current = parseDateLocal(currentReportDateStr);
    if (current && booking.getTime() <= current.getTime()) return null;

    const date = formatDateLocal(booking);
    return { date, nextScheduledDate: calculateNextDueDate(date, config) };
};

/**
 * Folgetermin nach dem VERSAND eines Berichts.
 *
 * Der Versand ist der Beleg, dass die Begehung stattgefunden hat — bis hierher
 * schrieb ihn aber nur der Dialog "Naechste Begehung anlegen" fort. Wer den
 * Bericht verschickte und den Folgebericht nie ueber den Dialog anlegte, bei
 * dem blieb der Termin beliebig lange stehen.
 *
 * Die Regel ist bewusst MONOTON: der Termin darf durch einen Versand nur nach
 * vorne wandern, nie zurueck. Das haelt drei Faelle von selbst richtig:
 *   - Wiederholungsversand desselben Berichts rechnet denselben Wert und
 *     aendert nichts.
 *   - Wurde der Folgebericht schon angelegt, steht der Termin bereits weiter
 *     vorne und bleibt stehen.
 *   - Ein nachgeholter Versand (Pending-Send-Replay) kann keinen inzwischen
 *     fortgeschrittenen Plan zurueckdrehen.
 *
 * `null` heisst: nichts anfassen — auch bei Intervall 'manual', denn dort
 * liefert calculateNextDueDate bewusst keinen Termin.
 */
export const planDueDateAfterSend = (
    reportDateStr: string | null | undefined,
    currentNextStr: string | null | undefined,
    config: IntervalConfig | null | undefined,
): string | null => {
    if (!config) return null;

    const computed = calculateNextDueDate(reportDateStr || '', config);
    const next = parseDateLocal(computed);
    if (!next) return null;

    const current = parseDateLocal(currentNextStr);
    if (current && current.getTime() >= next.getTime()) return null;

    return computed;
};

/**
 * Was `getEffectiveDueDate` an einem Projekt liest. Schmal und strukturell,
 * damit Listeneintrag (`ListProject`), Cockpit- und Tourzeile und Testfixtures
 * mit Teil-Metadaten gleichermassen passen — gelesen werden nur Spalten, die
 * auch der Listenauszug traegt, und aus `data` nur `metadata`.
 */
export type DueDateProject = Partial<Pick<ListProject, 'status' | 'next_scheduled_date' | 'last_inspection_date' | 'updated_at'>> & {
    data?: { metadata?: Partial<ReportMetadata> | null } | null;
};

export const getEffectiveDueDate = (project: DueDateProject): string | null => {
    const meta = project.data?.metadata as ReportMetadata;
    const status = project.status || meta?.globalData?.status;

    if (status === 'on_hold' || status === 'completed' || status === 'finished') return null;

    // SiFa-Betreuung hat KEINEN Begehungsturnus und damit keinen
    // Faelligkeitstermin — auch nicht mit `intervalConfig`: der fruehere
    // Statuswechsel Planung→Ausfuehrung schrieb `{type:'weekly'}` samt
    // `next_scheduled_date` auf jedes Projekt (D1 in docs/sifa-betreuung-plan.md),
    // und useReportActions schreibt fuer SiFa nie einen Turnus fort. Ein
    // Altwert darf die Betreuung nicht als ueberfaellig in die Listen holen;
    // Betriebsbegehungen im Turnus stehen im Fristenregister (Nr. 92).
    if (istSifaProjekt(meta)) return null;

    const nextDateStr = project.next_scheduled_date || meta?.intervalConfig?.nextScheduledDate;
    if (nextDateStr) {
        // ← Defensiv: nur zurückgeben, wenn parsebar — kein "NaN-NaN-NaN" durchreichen
        if (parseDateLocal(nextDateStr)) return nextDateStr;
    }

    if (status === 'active' && meta?.intervalConfig) {
        const lastDate = project.last_inspection_date || meta.date || project.updated_at;
        const computed = calculateNextDueDate(lastDate, meta.intervalConfig);
        return computed || null;
    }
    return null;
};

export const isSubstitutionActive = (start: string, end: string): boolean => {
    if (!start || !end) return false;
    const s = parseDateLocal(start);
    const e = parseDateLocal(end);
    if (!s || !e) return false;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    s.setHours(0, 0, 0, 0);
    e.setHours(23, 59, 59, 999);
    return now >= s && now <= e;
};

export const getStartOfWeek = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

export const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const isDateInRange = (dateStr: string | undefined, start: Date, end: Date): boolean => {
    if (!dateStr) return false;
    const d = parseDateLocal(dateStr);
    if (!d) return false;

    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end); e.setHours(23, 59, 59, 999);
    return d >= s && d <= e;
};

export const isDateInCurrentWeek = (dateStr?: string): boolean => {
    const today = new Date();
    const start = getStartOfWeek(today);
    const end = addDays(start, 6);
    return isDateInRange(dateStr, start, end);
};

export const getCalendarWeek = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

/** Standardturnus, wenn ein Projekt keine `intervalConfig` trägt (wie im Urlaubsplaner). */
export const DEFAULT_INTERVAL_CONFIG: IntervalConfig = { type: 'weekly' };

/**
 * Wirksamer Turnus eines Projekts für Fälligkeitsrechnungen.
 * Fehlende Konfiguration = wöchentlich (so plant der Urlaubsplaner seit jeher).
 * SiFa-Betreuungen haben keinen Begehungsturnus (siehe `getEffectiveDueDate`)
 * und liefern deshalb `manual`, auch wenn ein Altwert in `intervalConfig` steht.
 */
export const resolveIntervalConfig = (meta: ReportMetadata | undefined | null): IntervalConfig => {
    if (!meta) return DEFAULT_INTERVAL_CONFIG;
    if (istSifaProjekt(meta)) return { type: 'manual' };
    return meta.intervalConfig || DEFAULT_INTERVAL_CONFIG;
};

/**
 * Alle Begehungstermine eines Projekts, die in [startDate, endDate] fallen.
 *
 * Läuft den Turnus mit `calculateNextDueDate` ab, statt ihn in eine ICS-RRULE
 * zu übersetzen: `twice_monthly`, `custom` und vor allem `preferredWeekdays`
 * verschieben einzelne Termine und sind als Wiederholungsregel nicht
 * verlustfrei abbildbar. Für einen Urlaubszeitraum sind es ohnehin wenige
 * Termine — die zählen wir lieber exakt auf.
 *
 * Ankerwahl (gleiche Rangfolge wie `getEffectiveDueDate`):
 *  1. `explicitNextDate` (Spalte `next_scheduled_date`) oder
 *     `config.nextScheduledDate` — das ist der geplante nächste Termin.
 *     Verschieben/Überspringen im Dashboard ändert NUR diesen Wert, deshalb
 *     muss er gewinnen, sonst kündigt die Mail übersprungene Begehungen an.
 *  2. Sonst ab `lastInspectionDate` weiterrechnen (die ist erledigt, zählt
 *     selbst nicht mit).
 *  3. Sonst ab `fallbackAnchor` (Berichtsdatum) weiterrechnen.
 *
 * Wöchentlich mit MEHREREN Wochentagen (Wochenplan „Di + Do") heißt hier: an
 * jedem dieser Tage ein Termin — so zählt auch die Cockpit-Auslastung
 * (`appointmentDays`). `calculateNextDueDate` springt dort mit Mindestabstand
 * fünf Tage und liefert nur einen Termin je Woche; für das Dashboard bleibt
 * das unverändert, für die Vertretung wären es zu wenige.
 *
 * Derselbe Sprung steckt auch im GEPLANTEN Termin: nach einer Begehung am
 * Dienstag schreibt die App den nächsten Dienstag als Plan, der Donnerstag
 * dazwischen fehlte damit in Planer, Mail, ICS und Vertretungsliste.
 * `lastDoneDate` (Tag der letzten Begehung) schließt die Lücke: Ist der Plan
 * genau der automatische Wert `calculateNextDueDate(letzte Begehung)`, beginnt
 * die Reihe am ersten Wochentag nach der Begehung. Ein von Hand gesetzter Plan
 * — vorgezogen oder verschoben — bleibt, wie er ist.
 */
export const listDueDatesInRange = (
    config: IntervalConfig | undefined | null,
    fallbackAnchor: string | undefined | null,
    startDate: string,
    endDate: string,
    max = 60,
    explicitNextDate?: string | null,
    lastDoneDate?: string | null,
): string[] => {
    if (!config || config.type === 'manual') return [];
    const start = parseDateLocal(startDate);
    const end = parseDateLocal(endDate);
    if (!start || !end || start > end) return [];

    const startKey = formatDateLocal(start);
    const endKey = formatDateLocal(end);

    const weekdays = Array.from(new Set((config.preferredWeekdays || []).filter(d => Number.isInteger(d) && d >= 0 && d <= 6)));
    const everyWeekday = config.type === 'weekly' && weekdays.length >= 2;
    const step = (from: string): string =>
        everyWeekday ? (findWeekdayOnOrAfter(from, weekdays, 1) || '') : calculateNextDueDate(from, config);

    const plannedNext = [explicitNextDate, config.nextScheduledDate].find(d => !!d && !!parseDateLocal(d));
    let candidate: string;
    if (plannedNext) {
        candidate = formatDateLocal(parseDateLocal(plannedNext));
        const done = everyWeekday && lastDoneDate ? parseDateLocal(lastDoneDate) : null;
        if (done) {
            const doneKey = formatDateLocal(done);
            const automatisch = calculateNextDueDate(doneKey, config);
            const frueher = findWeekdayOnOrAfter(doneKey, weekdays, 1);
            if (frueher && candidate === automatisch && frueher < candidate) {
                candidate = frueher;
            }
        }
    } else {
        const anchor = [config.lastInspectionDate, fallbackAnchor].find(d => !!d && !!parseDateLocal(d));
        if (!anchor) return [];
        candidate = step(anchor);
    }
    // Der Wochentags-Modus hat keinen Rhythmus über Wochen hinweg: Termine vor
    // dem Zeitraum direkt überspringen, statt sie einzeln abzulaufen — sonst
    // erreicht die 400er-Schranke bei alter letzter Begehung den Zeitraum nie.
    if (everyWeekday && candidate && candidate < startKey) {
        candidate = findWeekdayOnOrAfter(startKey, weekdays, 0) || '';
    }

    const out: string[] = [];
    // Harte Schranke gegen Endlosschleifen bei kaputtem Turnus (z.B. customDays: 0).
    for (let i = 0; i < 400 && out.length < max; i++) {
        if (!candidate || !parseDateLocal(candidate)) break;
        if (candidate > endKey) break;
        if (candidate >= startKey) out.push(candidate);
        const next = step(candidate);
        if (!next || next <= candidate) break;
        candidate = next;
    }
    return out;
};
